<template>
  <div class="static-integration-card" data-testid="integration-card">
    <div class="flex-grow flex flex-col">
      <div>
        <h3 class="static-integration-name" data-testid="integration-name">{{ integration.name }}</h3>
        <p class="static-integration-description" data-testid="integration-description">{{ integration.description }}</p>
        <div v-if="integration.serverUrl" class="static-integration-server" data-testid="integration-server-url">
          <span class="static-integration-server-label">MCP URL</span>
          <code>{{ integration.serverUrl }}</code>
        </div>
      </div>
      <div class="static-integration-button">
        <template v-if="integration.disabled">
          <Button
            type="solid"
            variant="blue"
            size="regular"
            @click="$emit('requestIntegration', integration.name)"
            :data-testid="`request-integration-${integration.name.toLowerCase()}`"
            text="Request to enable"
          />
          <p class="text-xs text-zinc-500 dark:text-zinc-400 mt-1" data-testid="restriction-notice">
            Available on {{ isNewPlanFor2025 ? 'Scale' : 'Enterprise' }} plan.
          </p>
        </template>
        <template v-else>
          <a
            :href="integration.url"
            target="_blank"
            rel="noopener noreferrer"
            class="static-setup-button"
            @click="$emit('trackClick', integration.name)"
            :data-testid="`setup-button-${integration.name.toLowerCase()}`"
            >Setup</a
          >
          <p class="text-xs mt-1 invisible">&nbsp;</p>
        </template>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { storeToRefs } from 'pinia';
import { Button } from '@/storybook';
import type { StaticIntegration } from '../../store/useIntegrationStore';
import { useOrganizationStore } from '@/store/useOrganizationStore';

defineProps<{
  integration: StaticIntegration;
}>();

defineEmits<{
  trackClick: [integrationName: string];
  requestIntegration: [integrationName: string];
}>();

const { isNewPlanFor2025 } = storeToRefs(useOrganizationStore());
</script>

<style scoped>
.static-integration-card {
  @apply flex items-stretch gap-6 p-8 bg-white rounded-xl border border-slate-300 transition-all duration-200 ease-in-out;
  @apply dark:bg-zinc-800 dark:border-zinc-600;
}

.static-integration-card:hover {
  @apply shadow-md;
}

.static-integration-name {
  @apply text-xl font-semibold text-slate-900 mb-2;
  @apply dark:text-zinc-200;
}

.static-integration-description {
  @apply text-sm text-slate-600 leading-6 mb-4;
  @apply dark:text-zinc-400;
}

.static-integration-server {
  @apply flex flex-col gap-1 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700;
  @apply dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-300;
}

.static-integration-server-label {
  @apply font-semibold uppercase tracking-wide text-slate-500;
  @apply dark:text-zinc-500;
}

.static-integration-server code {
  @apply break-all font-mono text-slate-900;
  @apply dark:text-zinc-100;
}

.static-integration-button {
  @apply mt-auto pt-4;
}

.static-setup-button {
  @apply inline-block px-4 py-2 bg-slate-800 text-white no-underline rounded-md text-sm font-medium transition-colors duration-200;
  @apply hover:bg-slate-700;
  @apply dark:bg-zinc-600 dark:hover:bg-zinc-500;
}
</style>
